import type { components } from "@majitap/sdk";
import { queryOptions } from "@tanstack/react-query";
import { api, ok } from "./api";

export type TruckPosition = components["schemas"]["TruckPosition"];

/** Every truck's latest position (P1-22), refreshed as the driver apps send them. */
export const truckPositionsQuery = queryOptions({
  queryKey: ["tracking", "trucks"],
  queryFn: () => ok(api.GET("/v1/admin/tracking/trucks", {})),
  refetchInterval: 10_000,
  staleTime: 5_000,
});

type Point = { latitude?: number | null; longitude?: number | null; lat?: number; lng?: number } | null | undefined;
const latOf = (p: Point) => p?.latitude ?? p?.lat ?? null;
const lngOf = (p: Point) => p?.longitude ?? p?.lng ?? null;

/** Straight-line kilometres between two points, to one decimal; null when either is unknown. */
export function kmBetween(a: Point, b: Point): number | null {
  const [la, ga, lb, gb] = [latOf(a), lngOf(a), latOf(b), lngOf(b)];
  if (la === null || ga === null || lb === null || gb === null) return null;
  const rad = (x: number) => (x * Math.PI) / 180;
  const h = Math.sin(rad(lb - la) / 2) ** 2 + Math.cos(rad(la)) * Math.cos(rad(lb)) * Math.sin(rad(gb - ga) / 2) ** 2;
  return Math.round(2 * 6371 * Math.asin(Math.sqrt(h)) * 10) / 10;
}

/** "12 s", "4 min", "2 h": how long ago a position was taken. */
export function ageOf(iso: string, now = Date.now()) {
  // number-ok: seconds, not money
  const s = Math.max(0, Math.round((now - Date.parse(iso)) / 1000));
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  return `${Math.round(s / 3600)} h`;
}

/** What a truck's position says in a list: "2.4 km from Kabete · seen 20 s ago". */
export function whereabouts(position: TruckPosition | undefined, km: number | null, to: string | null) {
  if (!position) return "No location yet";
  if (position.stale) return `Not seen for ${ageOf(position.recorded_at)}`;
  const seen = `seen ${ageOf(position.recorded_at)} ago`;
  return km !== null && to ? `${km} km from ${to} · ${seen}` : seen;
}

/** A truck's state on the map: not seen lately, held at a customer, moving, or idle on shift. */
export type TruckState = "moving" | "idle" | "held" | "stale";

/** Moving at walking pace or more (5 km/h); slower is parked or queueing. */
const MOVING_MPS = 1.4;

export function truckState(p: Pick<TruckPosition, "stale" | "speed_mps" | "delivery">): TruckState {
  if (p.stale) return "stale";
  if (p.delivery?.status === "suspended") return "held";
  return (p.speed_mps ?? 0) >= MOVING_MPS ? "moving" : "idle";
}

// number-ok: a speed in km/h, not money
export const kmh = (mps: number | null) => (mps === null ? null : Math.round(mps * 3.6));

/** One line about a truck: "34 km/h · seen 12 s ago", "Idle · seen 30 s ago", "Not seen for 14 min". */
export function truckLine(p: TruckPosition) {
  const state = truckState(p);
  if (state === "stale") return `Not seen for ${ageOf(p.recorded_at)}`;
  const what = state === "moving" ? `${kmh(p.speed_mps)} km/h` : state === "held" ? "Held at the customer" : "Idle";
  return `${what} · seen ${ageOf(p.recorded_at)} ago`;
}

/** A truck's trail since `from` (P1-22), refreshed with the positions. */
export const trailQuery = (truckId: string, from: string) =>
  queryOptions({
    queryKey: ["tracking", "trail", truckId, from],
    queryFn: () =>
      ok(api.GET("/v1/admin/tracking/trucks/{id}/trail", { params: { path: { id: truckId }, query: { from } } })),
    refetchInterval: 10_000,
  });

/**
 * The points of an encoded polyline (Google's format, precision 5), as [lng, lat] for the map: a
 * delivered delivery's path (`DeliveryRecord.path_polyline`).
 */
export function decodePolyline(encoded: string): [number, number][] {
  const points: [number, number][] = [];
  let i = 0;
  let lat = 0;
  let lng = 0;
  const next = () => {
    let shift = 0;
    let result = 0;
    let byte: number;
    do {
      byte = encoded.charCodeAt(i++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    return result & 1 ? ~(result >> 1) : result >> 1;
  };
  while (i < encoded.length) {
    lat += next();
    lng += next();
    // number-ok: coordinates, not money
    points.push([lng / 1e5, lat / 1e5]);
  }
  return points;
}

export type EtaSettings = components["schemas"]["EtaSettings"];

/** Settings › Delivery ETA (P8-73): who sees the ETA and how it's worked out. */
export const etaSettingsQuery = queryOptions({
  queryKey: ["tracking", "eta-settings"],
  queryFn: () => ok(api.GET("/v1/admin/tracking/eta-settings", {})),
});
